import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { deburr } from 'lodash';
import Search, { SearchEvent } from 'carbon-react/lib/components/search';

import { UIFormPanelProps, UIDragListItemTypes, UIDualListPanel } from '@futrli/ui-components';

import { OrganisationsDragList } from './organisations-drag-list/organisations-drag-list';
import { useConsolidationContext } from '../../../consolidation-context/consolidation-context';
import styles from './select-organisations-panel.scss';

export const SelectOrganisationsPanel = (props: UIFormPanelProps) => {
  const {
    flags: { isMultiCurrency },
    settings: {
      handleAddOrganisation,
      handleRemoveOrganisation,
      isMaxOrgsLimitReached,
      selectedOrganisations,
      unselectedOrganisations
    }
  } = useConsolidationContext();

  const intl = useIntl();

  const [searchCriteria, setSearchCritertia] = useState('');

  const [currentDragItem, setCurrentDragItem] = useState({
    id: '',
    allowAdd: false,
    allowRemove: false
  });

  const filteredUnselectedOrganisations = useMemo(() => {
    return (unselectedOrganisations || []).filter(option => {
      const name = deburr(option.name.toLowerCase());
      const criteria = deburr(searchCriteria.toLowerCase());
      return name.indexOf(criteria) !== -1;
    });
  }, [searchCriteria, unselectedOrganisations]);

  const handleDragEnd = useCallback(() => {
    setCurrentDragItem({
      id: '',
      allowAdd: false,
      allowRemove: false
    });
  }, []);

  const handleDragStart = useCallback(
    (event: React.DragEvent, id: string) => {
      const isAlreadySelected = !!selectedOrganisations.find(org => org.id === id);
      setCurrentDragItem({
        id,
        allowAdd: !isAlreadySelected,
        allowRemove: isAlreadySelected
      });
    },
    [selectedOrganisations]
  );

  const handleDropOnSelectedOrgsList = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      handleAddOrganisation(currentDragItem.id);
      handleDragEnd();
    },
    [currentDragItem.id, handleAddOrganisation, handleDragEnd]
  );

  const handleDropOnUnselectedOrgsList = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      handleRemoveOrganisation(currentDragItem.id);
      handleDragEnd();
    },
    [currentDragItem.id, handleDragEnd, handleRemoveOrganisation]
  );

  const handleSearchChange = useCallback((event: SearchEvent) => {
    setSearchCritertia(event.target.value);
  }, []);

  return (
    <div onDragEnd={handleDragEnd} style={{ height: '100%' }}>
      <UIDualListPanel
        {...props}
        data-testid="SelectOrganisationsPanel"
        className={styles.container}
        search1={
          <Search
            label={intl.formatMessage({
              id: 'common.search_ellipsis',
              defaultMessage: 'Search...'
            })}
            value={searchCriteria}
            onChange={handleSearchChange}
          />
        }
        list1={
          <OrganisationsDragList
            allowDrop={currentDragItem.allowRemove}
            className={styles.list}
            isDragging={!!currentDragItem.id}
            isMaxOrgsLimitReached={isMaxOrgsLimitReached}
            isMultiCurrency={isMultiCurrency}
            mode="available"
            onDragStart={handleDragStart}
            onDrop={handleDropOnUnselectedOrgsList}
            organisations={filteredUnselectedOrganisations}
            title={
              <FormattedMessage
                id="consolidation.available_entities_to_add"
                defaultMessage="Available entities to add to your consolidation"
              />
            }
            type={UIDragListItemTypes.ADD}
          />
        }
        search2={<div />}
        list2={
          <OrganisationsDragList
            allowDrop={currentDragItem.allowAdd}
            className={styles.list}
            isDragging={!!currentDragItem.id}
            mode="selected"
            noOrgsContent={
              <FormattedMessage
                id="consolidation.drag_your_first_entity"
                defaultMessage="Please drag your first entity across"
              />
            }
            onClick={handleRemoveOrganisation}
            onDragStart={handleDragStart}
            onDrop={handleDropOnSelectedOrgsList}
            organisations={selectedOrganisations}
            showColumnHeaders={false}
            title={<FormattedMessage id="consolidation.consolidated_entities" defaultMessage="Consolidated entities" />}
            type={UIDragListItemTypes.REMOVE}
          />
        }
      />
    </div>
  );
};
